import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTelegramPlane } from 'react-icons/fa';
import LoginForm from './Login/LoginForm';
import OtpForm from './Login/OTPForm';
import PasswordForm from './Login/PasswordForm';
import RegisterForm from "./Login/RegisterForm";
import { getTelegramBotLink } from '../constants/whitelabelEnv';
import { domain } from '../constants/Branding';
import { setBrowser, setIsLoggedIn } from '../store/authReducer';
import TOKEN_API from '../services/token';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useTranslation } from '../hooks/useTranslation';

interface LoginPageProps {
  referalCode: any;
  theme?: 'default' | 'lovable';
  onRegisterClick?: () => void;
  backgroundColor?: string;
  inputBackgroundBox?: string;
  tableBorder?: string;
  primaryColor?: string;
  backgroundTeamInfo?: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ referalCode, theme = 'default', onRegisterClick, backgroundColor, inputBackgroundBox, tableBorder, primaryColor, backgroundTeamInfo }) => {
  const { t } = useTranslation();
  // Current step in the authentication flow
  const [step, setStep] = useState(1);
  const [demoLoading, setDemoLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const siteConfig = useSelector((state: RootState) => state.userDetails.siteConfig);
  const apkUrl = (siteConfig?.apkUrl || '').trim();
  const isAndroidDevice = /android/i.test(navigator.userAgent || "");
  const isIphoneDevice = /iphone|ipad|ipod/i.test(navigator.userAgent || "");
  const shouldShowApkButton = isAndroidDevice && !isIphoneDevice;
  // User data that will be collected throughout the process
  const [userData, setUserData] = useState({
    mobileNumber: '',
    countryCode: '91',
    password: '',
    confirmPassword: '',
    username: '',
    otpToken: '',
    userId: '',
    otpMessage: '',
  });

  // Whether the user exists in the system (determined after OTP verification)
  const [userExists, setUserExists] = useState<boolean | null>(null);

  // Remaining time for verification steps
  const [otpTimeRemaining, setOtpTimeRemaining] = useState(0);
  const [passwordTimeRemaining, setPasswordTimeRemaining] = useState(0);

  // Update user data through the flow
  const updateUserData = (data: Partial<typeof userData>) => {
    setUserData(prev => ({ ...prev, ...data }));
  };

  // Advance to next step in the flow
  const goToNextStep = () => {
    setStep(prevStep => prevStep + 1);
  };

  // Go back to previous step
  const goToPreviousStep = () => {
    setStep(prevStep => Math.max(1, prevStep - 1));
  };

  // Set the verification times
  const startOtpTimer = () => {
    setOtpTimeRemaining(90); // 90 seconds for OTP verification
  };

  const startPasswordTimer = () => {
    setPasswordTimeRemaining(300); // 5 minutes (300 seconds) for password entry
  };

  // Handle OTP verification result
  const handleOtpVerified = (exists: boolean, token: string) => {
    setUserExists(exists);
    updateUserData({ otpToken: token });
    startPasswordTimer();
    goToNextStep();
  };

  // Go directly to login step
  const goToLoginStep = () => {
    setStep(1);
  };

  const telegramLink = getTelegramBotLink(domain);
  const telegramLinkWithReferral =
    telegramLink && referalCode ? `${telegramLink}?start=${referalCode}` : telegramLink;

  // Helper function to calculate brightness from hex color
  const getColorBrightness = (color: string): number => {
    if (!color) return 0;
    
    // Remove # if present
    const hex = color.replace('#', '');
    
    // Handle 8-digit hex (with alpha) - use first 6 digits for RGB
    const rgbHex = hex.length === 8 ? hex.substring(0, 6) : hex.length === 6 ? hex : hex.substring(0, 6);
    
    // Convert hex to RGB
    const r = parseInt(rgbHex.substring(0, 2), 16);
    const g = parseInt(rgbHex.substring(2, 4), 16);
    const b = parseInt(rgbHex.substring(4, 6), 16);
    
    // Calculate brightness using relative luminance formula
    return (r * 299 + g * 587 + b * 114) / 1000;
  };

  // Helper function to get text color based on background color
  const getTextColorForBackground = (bgColor: string): string => {
    if (!bgColor) return '#FFFFFF';
    const brightness = getColorBrightness(bgColor);
    return brightness > 128 ? '#000000' : '#FFFFFF';
  };

  // Get text color based on backgroundColor prop
  const textColor = backgroundColor ? getTextColorForBackground(backgroundColor) : undefined;

  // Theme-based classes
  const containerBg = theme === 'lovable' 
    ? 'bg-[hsl(275_35%_16%)] border border-[hsl(275,90%,64%)]' 
    : 'bg-zinc-900';
  const containerRounded = theme === 'lovable' ? 'rounded-2xl' : 'rounded-lg';
  const innerBg = theme === 'lovable' 
    ? 'bg-[hsl(275,35%,16%)] border-[hsl(275,90%,64%)]' 
    : 'bg-zinc-800 border-zinc-700';
  const telegramBg = theme === 'lovable'
    ? 'bg-[hsl(275,35%,20%)] hover:bg-[hsl(275,35%,25%)]'
    : 'bg-zinc-700 hover:bg-zinc-600 border-zinc-600';
  const showBorder = theme === 'lovable' ? '' : 'border';
  const descriptionTextClass = theme === 'lovable' ? 'text-[hsl(275_50%_65%)]' : 'text-zinc-400';

  return (
    <div 
      className={`w-full max-w-md mx-auto p-6 pt-8 ${containerBg} ${textColor ? '' : 'text-white'} ${containerRounded} shadow-lg`}
      style={backgroundColor ? { backgroundColor, color: textColor, borderColor: tableBorder } : tableBorder ? { borderColor: tableBorder } : {}}
    >
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold" style={textColor ? { color: textColor } : {}}>
            {step === 1 && t('auth.signIn')}
            {step === 2 && t('auth.verifyOtp')}
            {step === 3 && t('auth.completeVerification')}
          </h2>
        </div>
        <p className={`text-sm ${descriptionTextClass}`} style={textColor ? { color: textColor } : {}}>
          {step === 1 && t('auth.pleaseEnterYourMobileNumber')}
          {step === 2 && t('auth.enterTheOtpSentToYourPhone')}
          {step === 3 && t('auth.completeYourAccountInformation')}
        </p>
      </div>

      {telegramLink ? (
        <div className={`rounded-lg ${innerBg} ${showBorder} mb-4`} style={backgroundTeamInfo ? { backgroundColor: backgroundTeamInfo } : {}}>
          <a
            href={telegramLinkWithReferral}
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline"
          >
            <div className={`flex items-center gap-1.5 sm:gap-2 p-2 sm:p-3 ${telegramBg} rounded-md cursor-pointer transition-colors ${showBorder}`} style={backgroundTeamInfo ? { backgroundColor: backgroundTeamInfo } : {}}>
              <FaTelegramPlane className="text-blue-400 w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span className="text-xs sm:text-sm text-blue-400">
                {t('auth.tryOutOurSiteAndPlayOnTelegram')}
              </span>
            </div>
          </a>
        </div>
      ) : null}

      {apkUrl && shouldShowApkButton && (
        <a
          href={apkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`mb-4 inline-flex w-full items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-border-color text-white no-underline`}
        >
          Download Android App
        </a>
      )}

      <div 
        className={`p-4 rounded-lg ${innerBg} border`}
        style={backgroundColor ? { backgroundColor, borderColor: tableBorder } : tableBorder ? { borderColor: tableBorder } : {}}
      >
        {step === 1 && (
          <LoginForm
            userData={userData}
            updateUserData={updateUserData}
            goToNextStep={goToNextStep}
            startOtpTimer={startOtpTimer}
            theme={theme}
            onRegisterClick={onRegisterClick}
            inputBackgroundBox={inputBackgroundBox}
            primaryColor={primaryColor}
            tableBorder={tableBorder}
            backgroundTeamInfo={backgroundTeamInfo}
            backgroundColor={backgroundColor}
          />
        )}

        {step === 2 && (
          <OtpForm
            userData={userData}
            timeRemaining={otpTimeRemaining}
            setTimeRemaining={setOtpTimeRemaining}
            onOtpVerified={handleOtpVerified}
            goToPreviousStep={goToPreviousStep}
            updateUserData={updateUserData}
          />
        )}

        {step === 3 && userExists === true && (
          <PasswordForm
            userData={userData}
            updateUserData={updateUserData}
            timeRemaining={passwordTimeRemaining}
            setTimeRemaining={setPasswordTimeRemaining}
            goToPreviousStep={goToPreviousStep}
            goToLoginStep={goToLoginStep}
          />
        )}

        {step === 3 && userExists === false && (
          <RegisterForm
            userData={userData}
            updateUserData={updateUserData}
            timeRemaining={passwordTimeRemaining}
            setTimeRemaining={setPasswordTimeRemaining}
            goToPreviousStep={goToPreviousStep}
            referal={referalCode}
            goToLoginStep={goToLoginStep}
          />
        )}
      </div>
    </div>
  );
};

export default LoginPage;